import { useAvatars } from "@/context/AvatarContext";

/**
 * Renders a circle avatar for an "owner" string (name or email).
 * If the user has uploaded a photo, that's used; otherwise a colored
 * initials circle. Optionally shows the name label alongside.
 */
export default function OwnerAvatar({
    owner,
    size = 22,
    showName = true,
    className = "",
    textClassName = "",
}) {
    const { resolveOwner } = useAvatars();
    const info = resolveOwner(owner);

    if (!info) {
        return (
            <span
                className={`font-mono text-xs text-virtus-faint ${className}`}
                data-testid="owner-empty"
            >
                —
            </span>
        );
    }

    const circleStyle = {
        width: size,
        height: size,
        minWidth: size,
        borderRadius: "50%",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
    };

    const initialsStyle = {
        ...circleStyle,
        background: `linear-gradient(135deg, ${info.color}, ${info.color}CC)`,
        color: "#000",
        fontFamily: "var(--font-mono, ui-monospace, monospace)",
        fontWeight: 700,
        fontSize: Math.round(size * 0.42),
        letterSpacing: "0.02em",
    };

    return (
        <span
            className={`inline-flex items-center gap-2 min-w-0 ${className}`}
            title={info.name}
            data-testid={`owner-avatar-${(info.email || info.name || "").replace(/[^a-z0-9]+/gi, "-")}`}
        >
            {info.avatar_url ? (
                <img
                    src={info.avatar_url}
                    alt={info.name}
                    style={circleStyle}
                    className="object-cover"
                />
            ) : (
                <span style={initialsStyle}>{info.initials}</span>
            )}
            {showName && (
                <span
                    className={`truncate font-mono text-xs ${textClassName || "text-virtus-dim"}`}
                >
                    {info.name}
                </span>
            )}
        </span>
    );
}
